import { motion } from 'framer-motion'
import { Tag } from './ui'
import { useT } from '../lib/i18n'

interface MethodPageProps {
  onEnterDemo: () => void
  onOpenLab: () => void
  onOpenIndustries: () => void
  onOpenPlaybook: () => void
}

export function MethodPage({
  onEnterDemo,
  onOpenLab,
  onOpenIndustries,
  onOpenPlaybook,
}: MethodPageProps) {
  return (
    <main>
      <MethodHero />
      <WhatIsSection />
      <WhyMattersSection />
      <HowToUseSection onOpenLab={onOpenLab} />
      <AdoptionSection />
      <BeyondBasicsSection onOpenPlaybook={onOpenPlaybook} />
      <NextSection
        onEnterDemo={onEnterDemo}
        onOpenLab={onOpenLab}
        onOpenIndustries={onOpenIndustries}
      />
    </main>
  )
}

// ───── Hero ─────

function MethodHero() {
  const m = useT().method.hero
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 pt-20 sm:pt-28 pb-10 sm:pb-14">
      <Tag kind="rule" className="text-[length:var(--text-micro)] mb-5">
        {m.tag}
      </Tag>
      <motion.h1
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="text-[color:var(--color-orion-text)] leading-[1.05] max-w-4xl"
        style={{ fontSize: 'var(--text-h1)', fontWeight: 600 }}
      >
        {m.title}
      </motion.h1>
      <p
        className="mt-4 mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-haze)]"
      >
        {m.subtitle}
      </p>
      <p
        className="mt-6 max-w-3xl text-[color:var(--color-orion-haze)]"
        style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}
      >
        {m.leadParagraph}
      </p>
    </section>
  )
}

// ───── §A What it is ─────

function WhatIsSection() {
  const s = useT().method.whatIs
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 py-16 sm:py-20">
      <Tag kind="rule" className="text-[length:var(--text-micro)]">
        {s.sectionTag}
      </Tag>
      <h2
        className="mt-3 text-[color:var(--color-orion-text)] leading-[1.1] max-w-4xl"
        style={{ fontSize: 'var(--text-h2)' }}
      >
        {s.title}
        <span className="text-[color:var(--color-orion-amber)]">{s.titleSpan}</span>
      </h2>

      <div className="mt-8 surface-night border-faint border-l-2 border-l-[color:var(--color-orion-amber)] rounded-[var(--radius-lg)] p-5 sm:p-6">
        <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
          {s.leadLine}
        </div>
        <p className="text-[color:var(--color-orion-text)]" style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}>
          {s.leadBody}
        </p>
      </div>

      {/* Comparison */}
      <div className="mt-10">
        <h3
          className="text-[color:var(--color-orion-text)] mb-4"
          style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}
        >
          {s.comparisonTitle}
        </h3>
        <div className="overflow-x-auto surface-night border-faint rounded-[var(--radius-lg)]">
          <table className="w-full text-[length:var(--text-small)]">
            <thead>
              <tr className="border-b border-[color:var(--color-orion-surface)]/40">
                <th className="text-left px-4 py-3 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]" />
                <th className="text-left px-4 py-3 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
                  {s.comparisonHeaderKg}
                </th>
                <th className="text-left px-4 py-3 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
                  {s.comparisonHeaderOnto}
                </th>
              </tr>
            </thead>
            <tbody>
              {s.comparisonRows.map((row, i) => (
                <tr key={i} className="border-b border-[color:var(--color-orion-surface)]/20 last:border-b-0">
                  <td className="px-4 py-3 text-[color:var(--color-orion-text)] font-medium">{row.label}</td>
                  <td className="px-4 py-3 text-[color:var(--color-orion-haze)]">{row.kg}</td>
                  <td className="px-4 py-3 text-[color:var(--color-orion-text)]">{row.onto}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Three layers */}
      <div className="mt-12">
        <h3
          className="text-[color:var(--color-orion-text)]"
          style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}
        >
          {s.layersTitle}
        </h3>
        <p className="mt-2 text-[color:var(--color-orion-haze)] max-w-3xl">{s.layersHint}</p>
        <div className="mt-5 grid sm:grid-cols-3 gap-4">
          {s.layers.map((layer, i) => (
            <div key={i} className="surface-night border-faint rounded-[var(--radius-lg)] p-5">
              <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
                {layer.tag}
              </div>
              <div className="text-[color:var(--color-orion-text)] font-medium mb-2" style={{ fontSize: 'var(--text-h4)' }}>
                {layer.name}
              </div>
              <p className="text-[color:var(--color-orion-haze)] text-[length:var(--text-small)] leading-relaxed">
                {layer.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Airline example */}
      <div className="mt-12 surface-night border-faint rounded-[var(--radius-lg)] p-6 sm:p-8">
        <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] mb-2">
          {s.exampleTag}
        </div>
        <div className="text-[color:var(--color-orion-text)] font-medium mb-3" style={{ fontSize: 'var(--text-h4)' }}>
          {s.exampleTitle}
        </div>
        <p className="text-[color:var(--color-orion-haze)] leading-relaxed">{s.exampleBody}</p>
      </div>
    </section>
  )
}

// ───── §B Why it matters ─────

function WhyMattersSection() {
  const s = useT().method.whyMatters
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 py-16 sm:py-20">
      <Tag kind="rule" className="text-[length:var(--text-micro)]">
        {s.sectionTag}
      </Tag>
      <h2
        className="mt-3 text-[color:var(--color-orion-text)] leading-[1.1] max-w-4xl"
        style={{ fontSize: 'var(--text-h2)' }}
      >
        {s.title}
        <span className="text-[color:var(--color-orion-amber)]">{s.titleSpan}</span>
      </h2>
      <p
        className="mt-4 max-w-3xl text-[color:var(--color-orion-haze)]"
        style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}
      >
        {s.lead}
      </p>

      {/* 6 challenges grid */}
      <div className="mt-10">
        <h3
          className="text-[color:var(--color-orion-text)] mb-5"
          style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}
        >
          {s.challengesTitle}
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {s.challenges.map((c, i) => (
            <div key={i} className="surface-night border-faint rounded-[var(--radius-lg)] p-5">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="mono text-[24px] leading-none text-[color:var(--color-orion-haze)] font-light">
                  {c.number}
                </span>
                <div className="text-[color:var(--color-orion-text)] font-medium" style={{ fontSize: 'var(--text-h4)' }}>
                  {c.title}
                </div>
              </div>
              <p className="text-[color:var(--color-orion-haze)] text-[length:var(--text-small)] leading-relaxed">
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Three values */}
      <div className="mt-12">
        <h3
          className="text-[color:var(--color-orion-text)]"
          style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}
        >
          {s.valuesTitle}
        </h3>
        <p className="mt-2 text-[color:var(--color-orion-haze)] max-w-3xl">{s.valuesHint}</p>
        <div className="mt-5 grid sm:grid-cols-3 gap-4">
          {s.values.map((v, i) => (
            <div key={i} className="surface-night border-faint border-l-2 border-l-[color:var(--color-orion-amber)] rounded-[var(--radius-lg)] p-5">
              <div className="text-[color:var(--color-orion-text)] font-medium mb-2" style={{ fontSize: 'var(--text-h4)' }}>
                {v.label}
              </div>
              <p className="text-[color:var(--color-orion-haze)] text-[length:var(--text-small)] leading-relaxed">
                {v.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Bridge */}
      <div className="mt-10 surface-night border-faint rounded-[var(--radius-xl)] p-6 sm:p-8 glow-amber border-[color:var(--color-orion-amber)]/40">
        <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-3">
          {s.bridgeTag}
        </div>
        <div className="text-[color:var(--color-orion-text)] mb-2" style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}>
          {s.bridgeTitle}
        </div>
        <p className="text-[color:var(--color-orion-haze)] leading-relaxed max-w-3xl">{s.bridgeBody}</p>
      </div>
    </section>
  )
}

// ───── §C How to use ─────

function HowToUseSection({ onOpenLab }: { onOpenLab: () => void }) {
  const s = useT().method.howToUse
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 py-16 sm:py-20">
      <Tag kind="rule" className="text-[length:var(--text-micro)]">
        {s.sectionTag}
      </Tag>
      <h2
        className="mt-3 text-[color:var(--color-orion-text)] leading-[1.1] max-w-4xl"
        style={{ fontSize: 'var(--text-h2)' }}
      >
        {s.title}
        <span className="text-[color:var(--color-orion-amber)]">{s.titleSpan}</span>
      </h2>
      <p
        className="mt-4 max-w-3xl text-[color:var(--color-orion-haze)]"
        style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}
      >
        {s.lead}
      </p>

      {/* Three capabilities */}
      <div className="mt-10">
        <h3
          className="text-[color:var(--color-orion-text)] mb-5"
          style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}
        >
          {s.capabilitiesTitle}
        </h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {s.capabilities.map((c, i) => (
            <div key={i} className="surface-night border-faint rounded-[var(--radius-lg)] p-5">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="mono text-[24px] leading-none text-[color:var(--color-orion-haze)] font-light">
                  {c.number}
                </span>
                <div className="text-[color:var(--color-orion-text)] font-medium" style={{ fontSize: 'var(--text-h4)' }}>
                  {c.title}
                </div>
              </div>
              <p className="text-[color:var(--color-orion-haze)] text-[length:var(--text-small)] leading-relaxed">
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Dual engines */}
      <div className="mt-12">
        <h3
          className="text-[color:var(--color-orion-text)]"
          style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}
        >
          {s.enginesTitle}
        </h3>
        <p className="mt-2 text-[color:var(--color-orion-haze)] max-w-3xl">{s.enginesHint}</p>
        <div className="mt-5 grid md:grid-cols-2 gap-4">
          {s.engines.map((e, i) => (
            <div key={i} className="surface-night border-faint rounded-[var(--radius-lg)] p-6">
              <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
                {e.tag}
              </div>
              <div className="text-[color:var(--color-orion-text)] font-medium mb-2" style={{ fontSize: 'var(--text-h4)' }}>
                {e.name}
              </div>
              <p className="text-[color:var(--color-orion-haze)] text-[length:var(--text-small)] leading-relaxed">
                {e.body}
              </p>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={onOpenLab}
          className="mt-4 mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-amber)] hover:text-[color:var(--color-orion-text)] transition-colors"
        >
          {s.enginesLinkLabel}
        </button>
      </div>

      {/* Context engineering table */}
      <div className="mt-12">
        <h3
          className="text-[color:var(--color-orion-text)]"
          style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}
        >
          {s.contextTitle}
        </h3>
        <p className="mt-2 text-[color:var(--color-orion-haze)] max-w-3xl">{s.contextHint}</p>
        <div className="mt-5 overflow-x-auto surface-night border-faint rounded-[var(--radius-lg)]">
          <table className="w-full text-[length:var(--text-small)]">
            <tbody>
              {s.contextRows.map((row, i) => (
                <tr key={i} className="border-b border-[color:var(--color-orion-surface)]/20 last:border-b-0">
                  <td className="px-4 py-3 text-[color:var(--color-orion-text)] font-medium align-top w-[28%]">{row.asset}</td>
                  <td className="px-4 py-3 text-[color:var(--color-orion-haze)]">{row.includes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

// ───── §D Adoption ─────

function AdoptionSection() {
  const s = useT().method.adoption
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 py-16 sm:py-20">
      <Tag kind="rule" className="text-[length:var(--text-micro)]">
        {s.sectionTag}
      </Tag>
      <h2
        className="mt-3 text-[color:var(--color-orion-text)] leading-[1.1] max-w-4xl"
        style={{ fontSize: 'var(--text-h2)' }}
      >
        {s.title}
        <span className="text-[color:var(--color-orion-amber)]">{s.titleSpan}</span>
      </h2>
      <p
        className="mt-4 max-w-3xl text-[color:var(--color-orion-haze)]"
        style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}
      >
        {s.lead}
      </p>

      {/* Phases */}
      <div className="mt-10">
        <h3
          className="text-[color:var(--color-orion-text)] mb-5"
          style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}
        >
          {s.phasesTitle}
        </h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {s.phases.map((p, i) => (
            <div key={i} className="surface-night border-faint rounded-[var(--radius-lg)] p-5 text-center">
              <div className="mono text-[color:var(--color-orion-amber)] mb-2" style={{ fontSize: 'var(--text-h3)' }}>
                {p.duration}
              </div>
              <div className="text-[color:var(--color-orion-haze)] text-[length:var(--text-small)]">{p.scope}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Six steps */}
      <div className="mt-12">
        <h3
          className="text-[color:var(--color-orion-text)] mb-5"
          style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}
        >
          {s.stepsTitle}
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {s.steps.map((step, i) => (
            <div key={i} className="surface-night border-faint rounded-[var(--radius-md)] p-4 flex items-baseline gap-3">
              <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-amber)]">
                0{i + 1}
              </span>
              <span className="text-[color:var(--color-orion-text)] text-[length:var(--text-small)]">{step}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Deployment modes */}
      <div className="mt-12">
        <h3
          className="text-[color:var(--color-orion-text)]"
          style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}
        >
          {s.deploymentTitle}
        </h3>
        <p className="mt-2 text-[color:var(--color-orion-haze)] max-w-3xl">{s.deploymentHint}</p>
        <div className="mt-5 overflow-x-auto surface-night border-faint rounded-[var(--radius-lg)]">
          <table className="w-full text-[length:var(--text-small)]">
            <tbody>
              {s.deploymentRows.map((row, i) => (
                <tr key={i} className="border-b border-[color:var(--color-orion-surface)]/20 last:border-b-0">
                  <td className="px-4 py-3 mono text-[color:var(--color-orion-amber)] w-[28%]">{row.mode}</td>
                  <td className="px-4 py-3 text-[color:var(--color-orion-haze)]">{row.fit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

// ───── §E Next ─────

function NextSection({
  onEnterDemo,
  onOpenLab,
  onOpenIndustries,
}: {
  onEnterDemo: () => void
  onOpenLab: () => void
  onOpenIndustries: () => void
}) {
  const s = useT().method.next
  const handlers: Record<string, () => void> = {
    demo: onEnterDemo,
    lab: onOpenLab,
    industries: onOpenIndustries,
  }
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 py-16 sm:py-20">
      <Tag kind="rule" className="text-[length:var(--text-micro)]">
        {s.sectionTag}
      </Tag>
      <h2
        className="mt-3 text-[color:var(--color-orion-text)] leading-[1.1] max-w-4xl"
        style={{ fontSize: 'var(--text-h2)' }}
      >
        {s.title}
        <span className="text-[color:var(--color-orion-amber)]">{s.titleSpan}</span>
      </h2>
      <p
        className="mt-4 max-w-3xl text-[color:var(--color-orion-haze)]"
        style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}
      >
        {s.lead}
      </p>
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        {s.cards.map((card, i) => {
          const onClick = handlers[card.target]
          return (
            <button
              key={i}
              type="button"
              onClick={onClick}
              className="text-left surface-night border-faint rounded-[var(--radius-lg)] p-6 hover:border-[color:var(--color-orion-amber)]/40 transition-colors group"
            >
              <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
                {card.tag}
              </div>
              <div className="text-[color:var(--color-orion-text)] font-medium mb-2" style={{ fontSize: 'var(--text-h4)' }}>
                {card.title}
              </div>
              <p className="text-[color:var(--color-orion-haze)] text-[length:var(--text-small)] leading-relaxed mb-4">
                {card.body}
              </p>
              <div className="mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-amber)] group-hover:text-[color:var(--color-orion-text)] transition-colors">
                {card.cta}
              </div>
            </button>
          )
        })}
      </div>
    </section>
  )
}

// ───── §F Beyond the basics ─────

function BeyondBasicsSection({ onOpenPlaybook }: { onOpenPlaybook: () => void }) {
  const s = useT().method.beyondBasics
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 py-16 sm:py-20">
      <Tag kind="rule" className="text-[length:var(--text-micro)]">
        {s.sectionTag}
      </Tag>
      <h2
        className="mt-3 text-[color:var(--color-orion-text)] leading-[1.1] max-w-4xl"
        style={{ fontSize: 'var(--text-h2)' }}
      >
        {s.title}
        <span className="text-[color:var(--color-orion-amber)]">{s.titleSpan}</span>
      </h2>
      <p
        className="mt-4 max-w-3xl text-[color:var(--color-orion-haze)]"
        style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}
      >
        {s.lead}
      </p>

      {/* Three layers */}
      <div className="mt-10">
        <h3 className="text-[color:var(--color-orion-text)]" style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}>
          {s.threeLayersTitle}
        </h3>
        <p className="mt-2 text-[color:var(--color-orion-haze)] max-w-3xl">{s.threeLayersHint}</p>
        <div className="mt-5 grid sm:grid-cols-3 gap-4">
          {s.threeLayers.map((layer, i) => (
            <div key={i} className="surface-night border-faint rounded-[var(--radius-lg)] p-5">
              <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
                {layer.tag}
              </div>
              <div className="text-[color:var(--color-orion-text)] font-medium mb-2" style={{ fontSize: 'var(--text-h4)' }}>
                {layer.name}
              </div>
              <p className="text-[color:var(--color-orion-haze)] text-[length:var(--text-small)] leading-relaxed">
                {layer.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Symptoms list */}
      <div className="mt-12">
        <h3 className="text-[color:var(--color-orion-text)]" style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}>
          {s.symptomsTitle}
        </h3>
        <p className="mt-2 text-[color:var(--color-orion-haze)] max-w-3xl">{s.symptomsHint}</p>
        <ul className="mt-5 space-y-2 max-w-3xl">
          {s.symptoms.map((symptom, i) => (
            <li key={i} className="flex items-baseline gap-3 text-[color:var(--color-orion-text)]">
              <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-amber)] shrink-0">
                0{i + 1}
              </span>
              <span>{symptom}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Contrast table */}
      <div className="mt-12">
        <h3 className="text-[color:var(--color-orion-text)]" style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}>
          {s.contrastTitle}
        </h3>
        <p className="mt-2 text-[color:var(--color-orion-haze)] max-w-3xl">{s.contrastHint}</p>
        <div className="mt-5 overflow-x-auto surface-night border-faint rounded-[var(--radius-lg)]">
          <table className="w-full text-[length:var(--text-small)]">
            <thead>
              <tr className="border-b border-[color:var(--color-orion-surface)]/40">
                <th className="text-left px-4 py-3 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]" />
                <th className="text-left px-4 py-3 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
                  {s.contrastHeaderShallow}
                </th>
                <th className="text-left px-4 py-3 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
                  {s.contrastHeaderDeep}
                </th>
              </tr>
            </thead>
            <tbody>
              {s.contrastRows.map((row, i) => (
                <tr key={i} className="border-b border-[color:var(--color-orion-surface)]/20 last:border-b-0">
                  <td className="px-4 py-3 text-[color:var(--color-orion-text)] font-medium">{row.label}</td>
                  <td className="px-4 py-3 text-[color:var(--color-orion-haze)]">{row.shallow}</td>
                  <td className="px-4 py-3 text-[color:var(--color-orion-text)]">{row.deep}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Playbook CTA */}
      <button
        type="button"
        onClick={onOpenPlaybook}
        className="mt-10 w-full text-left surface-night border-faint border-l-2 border-l-[color:var(--color-orion-amber)] rounded-[var(--radius-lg)] p-6 hover:border-[color:var(--color-orion-amber)]/60 transition-colors group"
      >
        <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
          {s.playbookCta.tag}
        </div>
        <div className="text-[color:var(--color-orion-text)] font-medium mb-2" style={{ fontSize: 'var(--text-h3)' }}>
          {s.playbookCta.title}
        </div>
        <p className="text-[color:var(--color-orion-haze)] leading-relaxed mb-3 max-w-3xl">{s.playbookCta.body}</p>
        <div className="mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-amber)] group-hover:text-[color:var(--color-orion-text)] transition-colors">
          {s.playbookCta.cta}
        </div>
      </button>
    </section>
  )
}
